import type { EndpointExpectedResponseMatchResultSchema } from './endpoints.schema';
import { EndpointExpectedResponseSchema } from './endpoints.schema';
import type { EndpointExpectedResponse } from './expected-responses.util';
import type { z } from 'zod';

export type EndpointExpectedResponseMatchResult = z.infer<
  typeof EndpointExpectedResponseMatchResultSchema
>;

export type MatchHarToExpectedResponsesResult = {
  matchResult: EndpointExpectedResponseMatchResult;
  matchedExpectedResponse: EndpointExpectedResponse | null;
};

type HarResponseForMatching = {
  status: number;
  body: string;
  headers: string;
};

const formatHeadersForMatching = (
  headers: ReadonlyArray<{ name: string; value: string }>,
): string => headers.map(({ name, value }) => `${name}: ${value}`).join('\n');

export const extractHarResponseForMatching = ({
  status,
  body,
  headers,
}: {
  status: number;
  body: string;
  headers: ReadonlyArray<{ name: string; value: string }>;
}): HarResponseForMatching => ({
  status,
  body,
  headers: formatHeadersForMatching(headers),
});

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Characters that keep a token "inside the same identifier": letters,
// digits, underscore, hyphen. Only checked at an edge when the token's own
// edge character is one of these — a token starting/ending with '/' or '.'
// (a path or hostname fragment) is already self-delimiting there and needs
// no extra check, but a token starting/ending with a plain word character
// does: otherwise a short literal like `order` or `type` silently matches
// inside `border` or `Content-Type`, and `/login` matches inside
// `/login_attempts` because only its *end* touches a continuing word char.
const WORD_CHAR = /[a-zA-Z0-9_-]/;
const WORD_CHAR_CLASS = '[a-zA-Z0-9_\\-]';

const tokenMatchesHaystack = (token: string, haystack: string): boolean => {
  const escaped = escapeRegExp(token);
  const leftBoundary = WORD_CHAR.test(token[0])
    ? `(?<!${WORD_CHAR_CLASS})`
    : '';
  const rightBoundary = WORD_CHAR.test(token[token.length - 1])
    ? `(?!${WORD_CHAR_CLASS})`
    : '';
  const pattern = new RegExp(`${leftBoundary}${escaped}${rightBoundary}`, 'i');

  return pattern.test(haystack);
};

// 3xx outcomes are defined by their redirect target, not incidental header
// noise (cookies, server banners, etc). Scoping the haystack to the body and
// the Location header specifically — instead of the full raw header blob —
// stops a coincidental substring hit elsewhere in headers from producing a
// false "full" match.
const REDIRECT_STATUS_CODES = new Set([301, 302, 303, 307, 308]);

const extractHeaderValue = (
  headers: string,
  headerName: string,
): string | null => {
  const match = headers.match(new RegExp(`^${headerName}:\\s*(.*)$`, 'im'));

  return match ? match[1].trim() : null;
};

// These headers are present on nearly every response regardless of what the
// endpoint actually returned, so a generated token that happens to overlap
// with one (e.g. "json" or "html" inside a Content-Type MIME string) full-
// matches almost any response — not because the indicator's real content
// was found. Genuinely useful headers (WWW-Authenticate, custom app
// headers, Retry-After) are left in the haystack; Location is handled
// separately for redirects.
const BOILERPLATE_HEADER_NAMES = new Set([
  'content-type',
  'content-length',
  'content-encoding',
  'set-cookie',
  'date',
  'connection',
  'server',
  'cache-control',
  'etag',
  'vary',
]);

const stripBoilerplateHeaders = (headers: string): string =>
  headers
    .split('\n')
    .filter((line) => {
      const name = line.slice(0, line.indexOf(':')).trim().toLowerCase();

      return !BOILERPLATE_HEADER_NAMES.has(name);
    })
    .join('\n');

const buildMatchHaystack = ({
  status,
  body,
  headers,
}: {
  status: number;
  body: string;
  headers: string;
}): string => {
  if (REDIRECT_STATUS_CODES.has(status)) {
    const location = extractHeaderValue(headers, 'location');

    return `${body}\n${location ?? ''}`;
  }

  return `${body}\n${stripBoilerplateHeaders(headers)}`;
};

const isWildcardIndicator = (indicator: string): boolean => {
  const trimmed = indicator.trim();

  return trimmed === '' || trimmed === '*';
};

// An indicator is one or more LITERAL tokens that appear verbatim in the
// response (an error/message code, a distinctive JSON key, or a redirect
// marker), optionally separated by '|' to allow alternatives. We match
// literally rather than as a raw regex — indicators are generated text, so
// unescaped metacharacters in them (e.g. '.', '(') would silently over- or
// under-match — but boundary-anchor each token so it can't match as a
// substring of a longer, unrelated word or path segment.
const indicatorMatchesResponse = ({
  indicator,
  haystack,
}: {
  indicator: string;
  haystack: string;
}): boolean => {
  if (isWildcardIndicator(indicator)) {
    return true;
  }

  return indicator
    .trim()
    .split('|')
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
    .some((token) => tokenMatchesHaystack(token, haystack));
};

// When multiple expected responses share a status code and none matches the
// indicator outright, score each candidate by how many of its meaningful
// tokens actually appear in the response — rather than discarding the
// ambiguity as an outright miss. A tie at the top score means the ambiguity
// is genuine and we should not guess.
const SCORE_MATCH_THRESHOLD = 0.5;

// No minimum length here: containsToken is already boundary-anchored, so a
// short-but-specific token like "id" is safe to score on its own merits.
// Dropping it would collapse an indicator like `invalid_id` down to just
// ["invalid"], letting it score a perfect 1.0 on a single generic word and
// beat a more specific sibling like `invalid_token` that actually earned it.
// Unlike tokenMatchesHaystack above, these tokens are always pure
// alphanumeric (this split strips everything else), so a simple
// "not touching another alnum char" boundary is sufficient here.
const tokenizeIndicator = (indicator: string): string[] =>
  indicator
    .split(/[^a-zA-Z0-9]+/)
    .map((token) => token.toLowerCase())
    .filter((token) => token.length > 0);

const containsToken = (haystack: string, token: string): boolean =>
  new RegExp(`(?:^|[^a-zA-Z0-9])${token}(?:$|[^a-zA-Z0-9])`).test(haystack);

// Weighted by character length, not a plain hit-count ratio: a near-universal
// JSON key like "id" or "ok" is one token out of two, so an unweighted ratio
// lets it alone carry a candidate to the 0.5 floor on pure coincidence — any
// body with an "id" field would do it. Weighting by length means a short,
// generic token can only ever contribute a small fraction of the score;
// reaching the floor still requires the more distinctive tokens to hit too.
//
// Path/hostname-like alternatives (containing '/' or '.') are the exception:
// tokenizeIndicator's split treats '_'/'-' as delimiters, which is right for
// a descriptive snake_case indicator but wrong for a path fragment like
// "/login" — splitting it down to just "login" and checking containment
// with a delimiter-only boundary (no '_'/'-' protection) undoes exactly the
// edge-aware anchoring tokenMatchesHaystack uses to keep "/login" from
// matching "/login_attempts". So score these atomically via
// tokenMatchesHaystack itself instead: full credit or none, no partial
// tokenization, consistent with how the literal-match path treats them.
const scoreAlternativeAgainstHaystack = (
  alternative: string,
  lowerHaystack: string,
): number => {
  if (/[/.]/.test(alternative)) {
    return tokenMatchesHaystack(alternative, lowerHaystack) ? 1 : 0;
  }

  const tokens = tokenizeIndicator(alternative);

  if (tokens.length === 0) {
    return 0;
  }

  const totalLength = tokens.reduce((sum, token) => sum + token.length, 0);
  const hitLength = tokens
    .filter((token) => containsToken(lowerHaystack, token))
    .reduce((sum, token) => sum + token.length, 0);

  return hitLength / totalLength;
};

// An indicator's '|'-separated alternatives are OR'd in the literal-match
// path (any one appearing is enough), so scoring must preserve that: score
// each alternative independently and take the best. Tokenizing the whole
// indicator in one pass would merge unrelated alternatives into a single
// AND-bag — e.g. "connection_lost|session_expired" against a body containing
// only "session" would dilute that real, on-topic partial match with
// "connection"/"lost"'s misses, potentially sinking a legitimate signal
// below threshold for no principled reason.
const scoreIndicatorAgainstHaystack = (
  indicator: string,
  lowerHaystack: string,
): number => {
  const alternatives = indicator
    .split('|')
    .map((alternative) => alternative.trim())
    .filter((alternative) => alternative.length > 0);

  if (alternatives.length === 0) {
    return 0;
  }

  return Math.max(
    ...alternatives.map((alternative) =>
      scoreAlternativeAgainstHaystack(alternative, lowerHaystack),
    ),
  );
};

// Scoring gives partial credit across multiple tokens, which makes it far
// more exposed to generic header noise than the exact match check above:
// a two-token indicator only needs one hit to clear SCORE_MATCH_THRESHOLD,
// and near-universal boilerplate like `Content-Type: application/json` or
// `Set-Cookie` can supply that hit for an otherwise-unrelated candidate. For
// non-redirect statuses, score against the body only; for redirects, the
// Location header is a real signal (not boilerplate), so buildMatchHaystack's
// existing body+Location scoping is used unchanged.
const buildScoringHaystack = ({
  status,
  body,
  headers,
}: {
  status: number;
  body: string;
  headers: string;
}): string =>
  REDIRECT_STATUS_CODES.has(status)
    ? buildMatchHaystack({ status, body, headers })
    : body;

// Body-only scoping (above) removes header boilerplate, but an HTML page
// (e.g. a login/SPA shell served as a 200) is itself mostly generic prose —
// nav links, boilerplate copy, common short words — which is exactly the
// kind of noise token-overlap scoring can't reliably distinguish from real
// signal. Rather than risk misattributing an HTML response to an unrelated
// JSON-oriented sibling on coincidental word overlap, skip scoring entirely
// for HTML and fall through to the wildcard/single-candidate/unmatched path.
const looksLikeHtml = (body: string, headers: string): boolean => {
  const contentType = extractHeaderValue(headers, 'content-type');

  if (contentType && /html/i.test(contentType)) {
    return true;
  }

  return /^\s*<(!doctype|html)/i.test(body);
};

const findBestScoringCandidate = (
  candidates: EndpointExpectedResponse[],
  haystack: string,
): EndpointExpectedResponse | null => {
  const lowerHaystack = haystack.toLowerCase();

  const scored = candidates
    .map((candidate) => ({
      candidate,
      score: scoreIndicatorAgainstHaystack(candidate.indicator, lowerHaystack),
    }))
    .filter(({ score }) => score >= SCORE_MATCH_THRESHOLD)
    .sort((a, b) => b.score - a.score);

  if (scored.length === 0) {
    return null;
  }

  if (scored.length > 1 && scored[0].score === scored[1].score) {
    return null;
  }

  return scored[0].candidate;
};

export const matchHarToExpectedResponses = ({
  status,
  body,
  headers,
  expectedResponses,
}: HarResponseForMatching & {
  expectedResponses: EndpointExpectedResponse[];
}): MatchHarToExpectedResponsesResult => {
  if (expectedResponses.length === 0) {
    return { matchResult: 'unmatched', matchedExpectedResponse: null };
  }

  const sameStatus = expectedResponses.filter(
    (expected) => expected.responseCode === status,
  );

  if (sameStatus.length === 0) {
    return { matchResult: 'unmatched', matchedExpectedResponse: null };
  }

  const haystack = buildMatchHaystack({ status, body, headers });

  // A wildcard ('' or '*') indicator always matches, so it must be tried
  // strictly last — after both the literal check AND scoring have had a
  // chance on the specific candidates. Array.prototype.find would otherwise
  // return the wildcard the moment it's reached, regardless of array order,
  // pre-empting scoring entirely and losing a real (if imperfect) attribution
  // to a more specific sibling in favour of the generic catch-all.
  const specificCandidates = sameStatus.filter(
    (expected) => !isWildcardIndicator(expected.indicator),
  );
  const wildcardCandidates = sameStatus.filter((expected) =>
    isWildcardIndicator(expected.indicator),
  );

  const literalMatch = specificCandidates.find((expected) =>
    indicatorMatchesResponse({
      indicator: expected.indicator,
      haystack,
    }),
  );

  if (literalMatch) {
    return {
      matchResult: 'full',
      matchedExpectedResponse:
        EndpointExpectedResponseSchema.parse(literalMatch),
    };
  }

  if (specificCandidates.length > 1 && !looksLikeHtml(body, headers)) {
    const scoringHaystack = buildScoringHaystack({ status, body, headers });
    const bestGuess = findBestScoringCandidate(
      specificCandidates,
      scoringHaystack,
    );

    if (bestGuess) {
      return {
        matchResult: 'status_only',
        matchedExpectedResponse:
          EndpointExpectedResponseSchema.parse(bestGuess),
      };
    }
  }

  if (wildcardCandidates.length > 0) {
    return {
      matchResult: 'full',
      matchedExpectedResponse: EndpointExpectedResponseSchema.parse(
        wildcardCandidates[0],
      ),
    };
  }

  if (sameStatus.length === 1) {
    return {
      matchResult: 'status_only',
      matchedExpectedResponse: EndpointExpectedResponseSchema.parse(
        sameStatus[0],
      ),
    };
  }

  return { matchResult: 'unmatched', matchedExpectedResponse: null };
};
